import { create } from 'zustand';
import api from '../api/client';
import i18n from '../../locales/i18n';
import { trackClientEvent } from '../../analytics/track';
import { queryClient } from '../queryClient';
import { useAuthStore } from './authStore';
import {
  sanitizeBootstrapAccountData,
  setConsentRequiredState,
} from '../api/consentPolicy';

const activeLocale = () => i18n.resolvedLanguage || i18n.language || 'en';
const PERSONAL_QUERY_ROOTS = new Set([
  'credits-payment-status',
  'gallery',
  'gallery-item',
  'history',
  'profile',
  'referral',
  'subscription-current',
  'subscription-payment-status',
  'user-history',
]);

function restrictPersonalData({ versionChanged = false } = {}) {
  setConsentRequiredState(true, { versionChanged });
  if (!versionChanged) {
    useAuthStore.getState().restrictAccountData();
  }
  queryClient.removeQueries({
    predicate: (query) => PERSONAL_QUERY_ROOTS.has(query.queryKey?.[0]),
  });
  queryClient.setQueryData(['bootstrap'], (current) =>
    sanitizeBootstrapAccountData(current, true));
}

const initialState = {
  consentRequired: null,
  currentVersion: null,
  acceptedVersion: null,
  hasConsentHistory: false,
  welcomeBonusCredits: 10,
  versionChanged: false,
  locale: activeLocale(),
  availableLocales: [],
  primaryLegalLocale: 'en',
  availableLocalesLoaded: false,
  isLoadingAvailableLocales: false,
  granted: {},
  isLoading: false,
  isSubmitting: false,
  error: null,
  modalOpen: false,
  deferredAction: null,
  consentTrigger: null,
};

export const useConsentStore = create((set, get) => ({
  ...initialState,

  requireConsent: (missing = []) => {
    restrictPersonalData({ versionChanged: get().versionChanged === true });
    set((state) => ({
      consentRequired: true,
      granted: {
        ...state.granted,
        ...Object.fromEntries(missing.map((type) => [type, false])),
      },
    }));
  },

  showConsentModal: (action, trigger) => {
    const normalizedTrigger = trigger || 'generate';
    set({
      modalOpen: true,
      deferredAction: typeof action === 'function' ? action : null,
      consentTrigger: normalizedTrigger,
      error: null,
    });
    trackClientEvent('consent_modal_shown', { trigger: normalizedTrigger });
  },

  dismissConsentModal: () => {
    const { modalOpen, consentTrigger } = get();
    if (modalOpen) {
      trackClientEvent('consent_modal_dismissed', {
        trigger: consentTrigger || 'generate',
      });
    }
    set({
      modalOpen: false,
      deferredAction: null,
      consentTrigger: null,
      error: null,
    });
  },

  completeDeferredConsent: (marketing) => {
    const { consentTrigger } = get();
    const trigger = consentTrigger || 'generate';

    set({
      modalOpen: false,
      deferredAction: null,
      consentTrigger: null,
    });

    trackClientEvent('consent_accepted', {
      trigger,
      marketing: Boolean(marketing),
    });
  },

  loadAvailableLocales: async () => {
    if (get().availableLocalesLoaded || get().isLoadingAvailableLocales) {
      return get().availableLocales;
    }
    set({ isLoadingAvailableLocales: true });
    try {
      const response = await api.get('/legal/available-locales');
      set({
        availableLocales: response.data.locales || [],
        primaryLegalLocale: response.data.primary || 'en',
        availableLocalesLoaded: true,
        isLoadingAvailableLocales: false,
      });
      return response.data.locales || [];
    } catch (error) {
      set({ isLoadingAvailableLocales: false });
      throw error;
    }
  },

  checkConsent: async ({ deferGrantedState = false } = {}) => {
    const locale = activeLocale();
    set({ isLoading: true, error: null, locale });
    try {
      const availableLocalesPromise = get().loadAvailableLocales().catch(() => []);
      const response = await api.get('/consent/status');
      await availableLocalesPromise;
      setConsentRequiredState(response.data.consentRequired, {
        versionChanged: response.data.versionChanged,
      });
      if (response.data.consentRequired) {
        restrictPersonalData({
          versionChanged: response.data.versionChanged === true,
        });
      }
      if (deferGrantedState && response.data.consentRequired === false) {
        return response.data;
      }
      set({
        ...response.data,
        locale,
        isLoading: false,
        error: null,
      });
      return response.data;
    } catch (error) {
      set({ isLoading: false, error });
      throw error;
    }
  },

  acceptConsent: async (payload) => {
    set({ isSubmitting: true, error: null });
    try {
      await api.post('/consent/accept', {
        ...payload,
        locale: payload.locale || get().locale || activeLocale(),
      });
      const consentStatus = await get().checkConsent({
        deferGrantedState: true,
      });
      if (consentStatus.consentRequired === false) {
        setConsentRequiredState(false, { versionChanged: false });
        await Promise.all([
          useAuthStore.getState().refreshUser(),
          queryClient.invalidateQueries({
            queryKey: ['bootstrap'],
            refetchType: 'active',
          }),
        ]);
        set({
          ...consentStatus,
          locale: get().locale,
          isLoading: false,
          error: null,
        });
      }
      return true;
    } catch (error) {
      set({ isSubmitting: false, error });
      throw error;
    } finally {
      set({ isSubmitting: false });
    }
  },

  revokeConsent: async (consentType) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await api.post('/consent/revoke', { consentType });
      await get().checkConsent();
      return response.data;
    } catch (error) {
      set({ isSubmitting: false, error });
      throw error;
    } finally {
      set({ isSubmitting: false });
    }
  },
}));
